// app/src/dashboard/widgets/InterestRatesWidget.tsx
import { useRef, useState, useEffect } from 'react'
import { useQuery } from 'wasp/client/operations'
import { getInterestRates } from 'wasp/client/operations'
import { useTranslations } from '../../client/i18n/useTranslations'
import { Card } from '../../client/components/ui/card'
import { CardSectionHeader } from '../../client/components/ui/card-section-header'
import { format } from 'date-fns'

type InterestRate = {
  id: string
  centralBank: string
  currency: string
  currentRate: number
  previousRate: number
  decisionDate: string | Date
  nextMeetingDate?: string | Date | null
  marketConsensus?: string | null
  consensusPercent?: number | null
}

const BANK_FLAGS: Record<string, string> = {
  FED: '🇺🇸',
  ECB: '🇪🇺',
  BOE: '🇬🇧',
  BOJ: '🇯🇵',
  RBA: '🇦🇺',
  SNB: '🇨🇭',
  BOC: '🇨🇦',
  RBNZ: '🇳🇿',
}

function RateDirection({ current, previous }: { current: number; previous: number }) {
  if (current > previous) return <span className="text-primary">↑</span>
  if (current < previous) return <span className="text-destructive">↓</span>
  return <span className="text-muted-foreground">→</span>
}

function ConsensusBadge({ consensus, percent }: { consensus: string; percent: number | null | undefined }) {
  const t = useTranslations()
  const labels: Record<string, string> = {
    hold: t.marketDataWidgets.interestRates.consensusHold,
    cut: t.marketDataWidgets.interestRates.consensusCut,
    hike: t.marketDataWidgets.interestRates.consensusHike,
  }
  const colors: Record<string, string> = {
    hold: 'bg-muted text-muted-foreground',
    cut: 'bg-destructive/20 text-destructive',
    hike: 'bg-primary/20 text-primary',
  }
  return (
    <span className={`text-[9px] rounded px-1.5 py-0.5 font-medium ${colors[consensus] ?? 'bg-muted text-muted-foreground'}`}>
      {labels[consensus] ?? consensus}{percent != null ? ` ${Math.round(percent)}%` : ''}
    </span>
  )
}

export function InterestRatesWidget() {
  const t = useTranslations()
  const { data: rates, isLoading, error } = useQuery(getInterestRates)
  const cardRef = useRef<HTMLDivElement>(null)
  const [isCompact, setIsCompact] = useState(false)

  useEffect(() => {
    const el = cardRef.current
    if (!el) return
    const observer = new ResizeObserver(([entry]) => {
      setIsCompact(entry.contentRect.height < 130)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const rateList = (rates ?? []) as InterestRate[]

  return (
    <Card className="flex flex-col h-full overflow-hidden" ref={cardRef}>
      {isLoading ? (
        <div className={`flex-1 p-2 grid gap-1.5 ${isCompact ? 'grid-cols-8' : 'grid-cols-4'}`}>
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="animate-pulse rounded bg-muted" style={{ minHeight: isCompact ? 40 : 56 }} />
          ))}
        </div>
      ) : error || !rates ? (
        <div className="flex items-center justify-center h-full text-muted-foreground text-sm p-4">
          {t.marketDataWidgets.newsFeed.error}
        </div>
      ) : isCompact ? (
        /* ── Mode 1 ligne : toutes les banks en colonnes égales, pas de scroll ── */
        <div className="flex h-full items-stretch p-1.5 gap-1.5" style={{ display: 'grid', gridTemplateColumns: `repeat(${rateList.length}, 1fr)` }}>
          {rateList.map((rate) => (
            <div
              key={rate.centralBank}
              className="flex flex-col justify-center border border-border/60 rounded px-2 py-1 min-w-0"
            >
              <div className="flex items-center justify-between gap-1">
                <span className="text-[9px] font-bold truncate">{BANK_FLAGS[rate.centralBank] ?? ''} {rate.centralBank}</span>
                <span className="text-[8px] text-muted-foreground font-mono shrink-0">{rate.currency}</span>
              </div>
              <div className="flex items-center gap-0.5">
                <span className="text-xs font-bold font-mono leading-none">{rate.currentRate.toFixed(2)}%</span>
                <RateDirection current={rate.currentRate} previous={rate.previousRate} />
              </div>
              <div className="text-[8px] text-muted-foreground/70 leading-none truncate">
                {rate.previousRate.toFixed(2)}% · {format(new Date(rate.decisionDate), 'dd/MM/yy')}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* ── Mode normal : header + grille 4 colonnes ── */
        <>
          <CardSectionHeader label={t.marketDataWidgets.interestRates.title} isDark={false} compact />
          <div className="flex-1 overflow-hidden p-2">
            <div className="grid grid-cols-8 gap-1.5 h-full">
              {rateList.map((rate) => (
                <div key={rate.centralBank} className="border border-border/60 rounded-md px-1.5 py-1.5 min-w-0">
                  <div className="flex items-center justify-between gap-0.5">
                    <span className="text-[13px] font-bold truncate">
                      {BANK_FLAGS[rate.centralBank] ?? ''} {rate.centralBank}
                    </span>
                    <span className="text-[11px] text-muted-foreground font-mono shrink-0">{rate.currency}</span>
                  </div>
                  <div className="flex items-center gap-0.5 mt-0.5">
                    <span className="text-base font-bold font-mono leading-none">{rate.currentRate.toFixed(2)}%</span>
                    <RateDirection current={rate.currentRate} previous={rate.previousRate} />
                  </div>
                  <div className="text-[11px] text-muted-foreground/70 mt-0.5 leading-none truncate">
                    prev. {rate.previousRate.toFixed(2)}% · {format(new Date(rate.decisionDate), 'dd/MM/yy')}
                  </div>
                  {rate.marketConsensus && (
                    <div className="mt-1">
                      <ConsensusBadge consensus={rate.marketConsensus} percent={rate.consensusPercent} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </Card>
  )
}
